import { useState, useEffect } from 'react';
import { AgentTrainingFlow } from '../../../types/flowTypes';
import { trainingFlowsApi } from '../../../services/trainingFlowsApi';
import { FolderOpen, CheckCircle2, Info } from 'lucide-react';

interface FlowSelectorProps {
  currentFlowId: string | null;
  agentId?: string;
}

export const FlowSelector = ({ currentFlowId, agentId }: FlowSelectorProps) => {
  const [activeFlow, setActiveFlow] = useState<AgentTrainingFlow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActiveFlow();
  }, [agentId, currentFlowId]);

  const loadActiveFlow = async () => {
    setLoading(true);
    try {
      if (agentId) {
        const flows = await trainingFlowsApi.listFlows(agentId);
        const active = flows.find(f => f.is_active) || null;
        setActiveFlow(active);
      } else {
        setActiveFlow(null);
      }
    } catch (error) {
      console.error('Error al cargar flujo activo:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground">Cargando...</div>;
  }

  if (!activeFlow) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No hay workflow activo</p>
        <p className="text-xs mt-1">Guarda un workflow para activarlo</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="flex items-start gap-2 p-3 bg-accent/50 rounded-lg border border-border">
        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-sm truncate">{activeFlow.nombre}</h3>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex-shrink-0">
              Activo
            </span>
          </div>
          {activeFlow.descripcion && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {activeFlow.descripcion}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Actualizado: {new Date(activeFlow.updated_at).toLocaleDateString()}
          </p>
        </div>
      </div>
      
      <div className="flex items-start gap-2 mt-3 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-xs text-blue-700 dark:text-blue-300">
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <p>
          Este es el único workflow conectado a este agente. Para cambiarlo, crea un nuevo workflow y guárdalo.
        </p>
      </div>
    </div>
  );
};
